import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { WORKLIST_STATUSES, encodeSearch, formatJobNumber, jobListSchema } from '@fieldops/shared'
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  FilterChip,
  ListHead,
  Page,
  PageHeader,
  SkeletonRows,
  StatStrip,
  StatTile as Tile,
  VISIT_STATUS_TONE,
  buttonClass,
  humanise,
  useOnNavy,
  type Tone,
} from '../components/ui.js'
import { Icon } from '../components/Icon.js'
import { NewJobDialog } from '../components/NewJobDialog.js'
import {
  DockTile,
  Group,
  KnockNotes,
  NextStepButton,
  ScreenHeader,
  VisitBadge,
  cardClass,
  hasKnockNotes,
  lengthOf,
  rowClass,
  telHref,
  timeRange,
  untilLabel,
  workAndSuburb,
  workOf,
} from '../components/field/kit.js'
import { cn } from '../lib/cn.js'
import { useIsTablet } from '../lib/media.js'
import {
  addressLine,
  durationBetween,
  formatDuration,
  formatTime,
  money as moneyFormat,
  shortName,
} from '../lib/format.js'
import { useJobHref } from '../lib/job-overlay.js'
import { useConnectionState } from '../lib/realtime.js'
import { useSession } from '../lib/session.js'
import { useTRPC, type RouterOutputs } from '../lib/trpc.js'
import { EVENT_TYPE_LABEL } from './schedule/EventBlock.js'
import { coordsOfAddress, hopsForStops } from './schedule/lib/travel.js'
import type { Coordinates } from './schedule/lib/geometry.js'

type Visit = RouterOutputs['schedule']['board'][number]
type CalendarEvent = RouterOutputs['schedule']['events'][number]
type Stop =
  | { kind: 'visit'; id: string; startAt: Date; endAt: Date; visit: Visit }
  | { kind: 'event'; id: string; startAt: Date; endAt: Date; event: CalendarEvent }

/** A technician can check in to their own booking without SCHEDULE_EDIT. */
function isMine(visit: Visit, userId: string | undefined): boolean {
  return Boolean(userId) && visit.assignments.some((assignment) => assignment.userId === userId)
}

function isLive(visit: Visit): boolean {
  return visit.status !== 'CANCELLED' && visit.status !== 'NO_SHOW'
}

function coordsOf(visit: Visit): Coordinates | null {
  const address = visit.job.site?.address
  if (address?.latitude == null || address.longitude == null) return null
  return { latitude: address.latitude, longitude: address.longitude }
}

function suburbOf(visit: Visit): string | null {
  const address = visit.job.site?.address
  return address?.suburb ?? address?.city ?? null
}

/**
 * The day's driving, using the board's own estimate.
 *
 * Stored travel minutes win; otherwise it is a straight line at 30 km/h, which
 * is why the tile says "direct" underneath. It is not a routing product
 * (decision 27), and the number is here to say whether the day is tight, not
 * to be navigated by.
 */
function asStops(visits: Visit[], events: CalendarEvent[]): Stop[] {
  return [
    ...visits.filter(isLive).map((visit) => ({
      kind: 'visit' as const,
      id: visit.id,
      startAt: new Date(visit.startAt),
      endAt: new Date(visit.endAt),
      visit,
    })),
    ...events.map((event) => ({
      kind: 'event' as const,
      id: event.id,
      startAt: new Date(event.startAt),
      endAt: new Date(event.endAt),
      event,
    })),
  ].sort((a, b) => a.startAt.getTime() - b.startAt.getTime())
}

function isUnfinished(stop: Stop): boolean {
  return stop.kind === 'visit' ? stop.visit.status !== 'COMPLETED' : !stop.event.completedAt
}

function minutesOfStop(stop: Stop): number {
  return (stop.endAt.getTime() - stop.startAt.getTime()) / 60_000
}

function driveMinutes(stops: Stop[]): number {
  return hopsForStops(
    stops.map((stop) => ({
      id: stop.id,
      startAt: stop.startAt,
      endAt: stop.endAt,
      coords:
        stop.kind === 'visit' ? coordsOf(stop.visit) : coordsOfAddress(stop.event.address),
      travelMinutes: stop.kind === 'visit' ? stop.visit.travelMinutes : null,
      occupiesPlace: stop.kind === 'visit' || Boolean(coordsOfAddress(stop.event.address)),
    })),
  ).reduce((total, hop) => total + hop.minutes, 0)
}

/**
 * The landing screen, and the one a technician lives on.
 *
 * Two layouts from one component, because they answer different questions.
 * In the field it is "what am I doing next", so the next booking is the whole
 * top of the screen and everything else compresses to a line. In the office it
 * is "how is the day going", so the same data becomes counts and a list.
 *
 * The field layout is mockup 1b. Its ink-900 panel is the only inverse surface
 * outside sign-in.
 */
export function Today() {
  const trpc = useTRPC()
  const { user, can } = useSession()
  const connection = useConnectionState()
  const [newJobOpen, setNewJobOpen] = useState(false)

  const dayStart = new Date()
  dayStart.setHours(0, 0, 0, 0)
  const dayEnd = new Date(dayStart)
  dayEnd.setDate(dayEnd.getDate() + 1)

  const visits = useQuery({
    ...trpc.schedule.board.queryOptions({ from: dayStart, to: dayEnd }),
    enabled: can('SCHEDULE_VIEW'),
  })
  const eventsQuery = useQuery({
    ...trpc.schedule.events.queryOptions({ from: dayStart, to: dayEnd }),
    enabled: can('SCHEDULE_VIEW'),
  })

  const timer = useQuery({
    ...trpc.jobs.runningTimer.queryOptions(),
    enabled: can('JOB_VIEW'),
    refetchInterval: 60_000,
  })

  const all = visits.data ?? []
  const events = eventsQuery.data ?? []
  const mine = all.filter(
    (visit) => !user || visit.assignments.some((assignment) => assignment.userId === user.id),
  )
  const myEvents = user
    ? events.filter((event) => event.attendees.some((attendee) => attendee.userId === user.id))
    : events

  /*
   * The two panes deliberately read different sets.
   *
   * In the field the question is "what am I doing", so it is my bookings, and
   * only the whole day when I am assigned to none of it — otherwise a
   * dispatcher gets an empty screen. In the office the question is "how is the
   * day going", which is always the whole day: narrowing that to the
   * dispatcher's own visits would hide the unassigned work they are the only
   * person who can fix.
   */
  const fieldVisits = (mine.length > 0 ? mine : all).filter(isLive)
  const fieldEvents = mine.length > 0 ? myEvents : events
  const fieldStops = asStops(fieldVisits, fieldEvents)
  const unfinished = fieldStops.filter(isUnfinished)

  // Standing on site beats the next booking: a screen that says "next up" to
  // somebody who is already at a job is answering a question nobody asked.
  const heroOnSite = unfinished.find(
    (stop) => stop.kind === 'visit' && stop.visit.status === 'ON_SITE',
  )
  const hero = heroOnSite ?? unfinished[0]

  const officeLive = all.filter(isLive)
  const officeStops = asStops(officeLive, events)

  return (
    <Page width="xwide">
      <FieldToday
        pending={visits.isPending || eventsQuery.isPending}
        live={fieldStops}
        done={fieldStops.filter((stop) => !isUnfinished(stop))}
        hero={hero}
        later={unfinished.filter((stop) => stop.id !== hero?.id)}
        connection={connection}
        userId={user?.id}
        canEdit={can('SCHEDULE_EDIT')}
      />

      <OfficeToday
        pending={visits.isPending || eventsQuery.isPending}
        live={officeLive}
        stops={officeStops}
        onSite={officeLive.find((visit) => visit.status === 'ON_SITE')}
        timer={timer.data}
        onNewJob={() => setNewJobOpen(true)}
      />

      <NewJobDialog open={newJobOpen} onClose={() => setNewJobOpen(false)} />
    </Page>
  )
}

/* -------------------------------------------------------------------------- */
/* Field                                                                      */
/* -------------------------------------------------------------------------- */

function FieldToday({
  pending,
  live,
  done,
  hero,
  later,
  connection,
  userId,
  canEdit,
}: {
  pending: boolean
  live: Stop[]
  done: Stop[]
  hero: Stop | undefined
  later: Stop[]
  connection: string
  userId: string | undefined
  canEdit: boolean
}) {
  const jobHref = useJobHref()
  const { user } = useSession()
  const tablet = useIsTablet()

  const header = (
    <ScreenHeader
      eyebrow={new Date().toLocaleDateString('en-AU', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      })}
      title="Today"
      action={
        tablet ? null : (
          <Link to="/me" aria-label="Me" className="block rounded-full">
            <Avatar
              name={user?.name ?? '?'}
              colour={user?.colour}
              className="size-[38px] text-[13px]"
            />
          </Link>
        )
      }
    />
  )

  return (
    <div className="flex flex-col gap-3.5 desk:hidden">
      {header}

      {/* Only ever a warning. There is no green "connected" state, because a
          working connection is not news. */}
      {connection !== 'open' ? (
        <p className="flex items-start gap-2 rounded-xl bg-amber-50 px-3.5 py-2.5 text-[13.5px] leading-snug text-amber-900 ring-1 ring-amber-200 ring-inset">
          <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-amber-700" />
          {connection === 'connecting'
            ? 'Reconnecting…'
            : 'Offline. Report answers are kept on this device; everything else waits for signal.'}
        </p>
      ) : null}

      {pending ? (
        <div className={cardClass}>
          <SkeletonRows rows={3} />
        </div>
      ) : (
        <>
          <div className="flex gap-2">
            <StatTile
              label="Done"
              value={String(done.length)}
              suffix={` / ${live.length}`}
            />
            <StatTile label="On the road" value={formatDuration(driveMinutes(live))} hint="direct" />
            <StatTile
              label="Booked"
              value={formatDuration(live.reduce((total, stop) => total + minutesOfStop(stop), 0))}
            />
          </div>

          {hero ? (
            hero.kind === 'event' ? (
              <NextUpEvent event={hero.event} userId={userId} canEdit={canEdit} />
            ) : (
              <NextUp visit={hero.visit} userId={userId} canEdit={canEdit} />
            )
          ) : (
            <div className={cn(cardClass, 'py-4')}>
              <EmptyState
                title={live.length > 0 ? 'All done for today' : 'Nothing booked today'}
                description="Enjoy the quiet one."
              />
            </div>
          )}

          {later.length > 0 ? (
            <Group
              label="Later today"
              aside={`${later.length} ${later.length === 1 ? 'booking' : 'bookings'}`}
            >
              {later.map((stop) =>
                stop.kind === 'event' ? (
                  <div key={stop.id} className={rowClass}>
                    <TimeColumn startAt={stop.startAt} endAt={stop.endAt} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[16px] font-semibold text-ink-900">
                        {stop.event.title}
                      </p>
                      <p className="truncate text-[13px] text-ink-500">
                        {suburbOfAddress(stop.event.address) ?? EVENT_TYPE_LABEL[stop.event.type]}
                      </p>
                    </div>
                    <span className="rounded-md bg-ink-100 px-2 py-0.5 text-xs font-medium text-ink-600 ring-1 ring-ink-200 ring-inset">
                      {EVENT_TYPE_LABEL[stop.event.type]}
                    </span>
                  </div>
                ) : (
                  <Link key={stop.id} to={jobHref(stop.visit.job.id)} className={rowClass}>
                    <TimeColumn startAt={stop.startAt} endAt={stop.endAt} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[16px] font-semibold text-ink-900">
                        {stop.visit.job.client.name}
                      </p>
                      <p className="truncate text-[13px] text-ink-500">
                        {workAndSuburb(stop.visit.job, stop.visit.job.site?.address)}
                      </p>
                    </div>
                    <VisitBadge status={stop.visit.status} />
                  </Link>
                ),
              )}
            </Group>
          ) : null}

          {done.length > 0 ? (
            <Group label="Done" aside={String(done.length)}>
              {done.map((stop) => {
                const body = (
                  <>
                    <span className="grid size-6 shrink-0 place-items-center rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 ring-inset">
                      <Icon name="check" className="size-3.5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[15px] font-medium text-ink-700">
                        {stop.kind === 'visit' ? stop.visit.job.client.name : stop.event.title}
                      </p>
                      <p className="truncate text-[13px] text-ink-500">
                        {stop.kind === 'visit'
                          ? workAndSuburb(stop.visit.job, stop.visit.job.site?.address)
                          : EVENT_TYPE_LABEL[stop.event.type]}
                      </p>
                    </div>
                    <span className="shrink-0 text-[13px] tabular-nums text-ink-500">
                      {formatTime(stop.startAt)}
                    </span>
                  </>
                )
                return stop.kind === 'visit' ? (
                  <Link key={stop.id} to={jobHref(stop.visit.job.id)} className={rowClass}>
                    {body}
                  </Link>
                ) : (
                  <div key={stop.id} className={rowClass}>
                    {body}
                  </div>
                )
              })}
            </Group>
          ) : null}
        </>
      )}
    </div>
  )
}

function suburbOfAddress(
  address: { suburb?: string | null; city?: string | null } | null | undefined,
): string | null {
  return address?.suburb ?? address?.city ?? null
}

function TimeColumn({ startAt, endAt }: { startAt: Date; endAt: Date }) {
  return (
    <div className="w-[70px] shrink-0 whitespace-nowrap">
      <p className="text-[15px] font-semibold tabular-nums text-ink-900">{formatTime(startAt)}</p>
      <p className="text-xs tabular-nums text-ink-500">{lengthOf(startAt, endAt)}</p>
    </div>
  )
}

function StatTile({
  label,
  value,
  suffix,
  hint,
}: {
  label: string
  value: string
  suffix?: string
  hint?: string
}) {
  return (
    <div className={cn(cardClass, 'flex-1 px-3 py-2.5')}>
      <p className="text-[11px] font-semibold tracking-[0.04em] text-ink-500 uppercase">{label}</p>
      <p className="mt-0.5 font-display text-[22px] leading-tight font-extrabold tabular-nums text-brand-800">
        {value}
        {suffix ? <span className="text-sm font-medium text-ink-400">{suffix}</span> : null}
      </p>
      {hint ? <p className="text-[11px] text-ink-400">{hint}</p> : null}
    </div>
  )
}

/**
 * The next booking, or the one being stood on.
 *
 * Everything a person needs between the van and the front door, so the job
 * screen is a choice rather than a step: where, who, how long, what the site
 * will do to you, and the one button that moves the visit along. The dark
 * header opens the job.
 */
function NextUp({
  visit,
  userId,
  canEdit,
}: {
  visit: Visit
  userId: string | undefined
  canEdit: boolean
}) {
  const jobHref = useJobHref()
  const onSite = visit.status === 'ON_SITE'
  const enRoute = visit.status === 'EN_ROUTE'
  const phone = visit.job.client.phone
  const label = onSite
    ? 'On site now'
    : enRoute
      ? `On your way · ${formatTime(visit.startAt)}`
      : `Next up · ${formatTime(visit.startAt)}`

  return (
    <article className="overflow-hidden rounded-[18px] bg-white ring-1 ring-ink-200 ring-inset">
      <Link to={jobHref(visit.job.id)} className="block bg-brand-800 px-[18px] pt-4 pb-4">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[12px] font-semibold tracking-[0.04em] text-sky uppercase">
            {label}
          </p>
          <span className="shrink-0 text-[13px] tabular-nums text-sky/70">
            {onSite
              ? durationBetween(visit.arrivedAt ?? visit.startAt)
              : untilLabel(visit.startAt)}
          </span>
        </div>

        <p className="mt-2 font-display text-2xl leading-tight font-extrabold text-white">
          {visit.job.client.name}
        </p>
        {visit.job.site ? (
          <p className="mt-1 text-[15px] font-medium text-sky">{addressLine(visit.job.site.address)}</p>
        ) : null}
        <p className="mt-1 text-[13px] text-sky/70">
          {workOf(visit.job) ? `${workOf(visit.job)} · ` : ''}
          <span className="font-display font-bold text-white">{formatJobNumber(visit.job)}</span>
        </p>
      </Link>

      <div className="flex flex-col gap-3 p-4">
        <p className="text-sm text-ink-600">
          <span className="tabular-nums">
            {timeRange(visit.startAt, visit.endAt)} · {lengthOf(visit.startAt, visit.endAt)}
          </span>
          {visit.notes ? ` · ${visit.notes}` : ''}
        </p>

        {hasKnockNotes(visit.job.site) ? <KnockNotes site={visit.job.site} /> : null}

        {isMine(visit, userId) || canEdit ? (
          <div className="flex items-end gap-2.5">
            <NextStepButton visitId={visit.id} status={visit.status} />
            {phone ? <DockTile icon="phone" label="Call" href={telHref(phone)} /> : null}
          </div>
        ) : (
          <div className="flex items-center gap-2.5">
            <VisitBadge status={visit.status} />
            {phone ? (
              <a href={telHref(phone) ?? undefined} className="text-sm font-medium text-brand-700">
                Call {visit.job.client.name}
              </a>
            ) : null}
          </div>
        )}
      </div>
    </article>
  )
}

function NextUpEvent({
  event,
  userId,
  canEdit,
}: {
  event: CalendarEvent
  userId: string | undefined
  canEdit: boolean
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const hrefFor = useJobHref()
  const jobHref = event.jobId ? hrefFor(event.jobId) : null
  const assigned = Boolean(userId) && event.attendees.some((attendee) => attendee.userId === userId)
  const phone = event.phone
  const place = event.address ? addressLine(event.address) : null

  const complete = useMutation(
    trpc.schedule.completeEvent.mutationOptions({
      onSuccess: () => void queryClient.invalidateQueries(trpc.schedule.pathFilter()),
    }),
  )

  return (
    <article className="overflow-hidden rounded-[18px] bg-white ring-1 ring-ink-200 ring-inset">
      <div className="bg-ink-100 px-[18px] pt-4 pb-4 shadow-[inset_0_-1px_0_var(--color-ink-200)]">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[12px] font-semibold tracking-[0.04em] text-ink-500 uppercase">
            Next up · {formatTime(event.startAt)}
          </p>
          <span className="rounded-md bg-white px-2 py-0.5 text-xs font-medium text-ink-600 ring-1 ring-ink-200 ring-inset">
            {EVENT_TYPE_LABEL[event.type]}
          </span>
        </div>
        <p
          className={cn(
            'mt-2 text-2xl leading-tight font-bold text-ink-900',
            event.completedAt && 'text-ink-400 line-through',
          )}
        >
          {event.title}
        </p>
        {place ? <p className="mt-1 text-[15px] text-ink-600">{place}</p> : null}
      </div>

      <div className="flex flex-col gap-3 p-4">
        <p className="text-sm text-ink-600">
          <span className="tabular-nums">
            {timeRange(event.startAt, event.endAt)} · {lengthOf(event.startAt, event.endAt)}
          </span>
          {event.note ? ` · ${event.note}` : ''}
        </p>
        <div className="flex items-end gap-2.5">
          {event.type === 'TASK' && (assigned || canEdit) ? (
            <button
              type="button"
              disabled={complete.isPending}
              onClick={() => complete.mutate({ id: event.id, done: !event.completedAt })}
              className="flex h-[54px] min-w-0 flex-1 items-center justify-center rounded-[14px] bg-brand-600 text-[17px] font-semibold text-white active:bg-brand-700 disabled:opacity-70"
            >
              {event.completedAt ? 'Not done' : 'Done'}
            </button>
          ) : (
            <span className="flex-1" />
          )}
          {phone ? <DockTile icon="phone" label="Call" href={telHref(phone)} /> : null}
          {event.job && jobHref ? (
            <Link
              to={jobHref}
              className="flex h-[54px] items-center rounded-[14px] bg-ink-100 px-4 text-[15px] font-semibold text-ink-900 ring-1 ring-ink-200 ring-inset"
            >
              Open {formatJobNumber(event.job)}
            </Link>
          ) : null}
        </div>
      </div>
    </article>
  )
}

/* -------------------------------------------------------------------------- */
/* Office                                                                     */
/* -------------------------------------------------------------------------- */

type DayFilter = 'all' | 'unassigned' | 'unfinished'

const BOOKING_GRID =
  'grid-cols-[6.5rem_5rem_minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,0.9fr)_6.5rem_8rem]'

function BoardLink() {
  const onNavy = useOnNavy()
  return (
    <Link to="/board" className={buttonClass({ onNavy })}>
      <Icon name="schedule" className="size-4" />
      Open board
    </Link>
  )
}

function greeting(now: Date): string {
  const hour = now.getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

function OfficeToday({
  pending,
  live,
  stops,
  onSite,
  timer,
  onNewJob,
}: {
  pending: boolean
  live: Visit[]
  stops: Stop[]
  onSite: Visit | undefined
  timer: RouterOutputs['jobs']['runningTimer'] | undefined
  onNewJob: () => void
}) {
  const jobHref = useJobHref()
  const { user } = useSession()
  const [filter, setFilter] = useState<DayFilter>('all')

  const technicians = new Set(
    live.flatMap((visit) => visit.assignments.map((assignment) => assignment.userId)),
  )

  const counts = {
    unassigned: live.filter((visit) => visit.assignments.length === 0).length,
    enRoute: live.filter((visit) => visit.status === 'EN_ROUTE').length,
    onSite: live.filter((visit) => visit.status === 'ON_SITE').length,
    completed: live.filter((visit) => visit.status === 'COMPLETED').length,
  }

  const rows = stops.filter((stop) => {
    if (filter === 'unassigned') {
      return stop.kind === 'visit'
        ? stop.visit.assignments.length === 0
        : stop.event.attendees.length === 0
    }
    if (filter === 'unfinished') return isUnfinished(stop)
    return true
  })

  const now = new Date()
  const firstName = user?.name?.split(/\s+/)[0]

  return (
    <div className="hidden flex-col gap-[var(--stack-gap)] desk:flex">
      <PageHeader
        title={`${greeting(now)}${firstName ? `, ${firstName}` : ''}`}
        description={
          <>
            {now.toLocaleDateString('en-AU', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}{' '}
            · {stops.length} {stops.length === 1 ? 'stop' : 'stops'}
            {technicians.size > 0
              ? ` across ${technicians.size} ${technicians.size === 1 ? 'technician' : 'technicians'}`
              : ''}
          </>
        }
        actions={
          <>
            <BoardLink />
            <Button
              variant="primary"
              className="rounded-full"
              onClick={onNewJob}
              icon={<Icon name="plus" className="size-4" />}
            >
              New job
            </Button>
          </>
        }
      />

      {/* Unassigned is the only amber number on the screen, because it is the
          only one a dispatcher is expected to do something about. It is also
          the filter for the list below, so the number leads to the work. */}
      <StatStrip>
        <Tile label="Booked today" value={stops.length} caption="Jobs, tasks and breaks" />
        <Tile
          label="Unassigned"
          value={counts.unassigned}
          tone={counts.unassigned > 0 ? 'amber' : undefined}
          caption="Booked with nobody on it"
          onClick={() => setFilter(filter === 'unassigned' ? 'all' : 'unassigned')}
          active={filter === 'unassigned'}
        />
        <Tile label="En route" value={counts.enRoute} caption="On the road now" />
        <Tile label="On site" value={counts.onSite} caption="Working now" />
        <Tile
          label="Completed"
          value={counts.completed}
          tone={counts.completed > 0 ? 'green' : undefined}
          caption="Finished today"
        />
      </StatStrip>

      <div className="grid grid-cols-[minmax(0,1fr)_20rem] items-start gap-[var(--stack-gap)]">
        <div className="flex min-w-0 flex-col gap-[var(--stack-gap)]">
          {onSite || timer ? <OnSiteNow visit={onSite} timer={timer} /> : null}

          <Card>
            <CardHeader
              title="Bookings"
              action={
                <div className="flex gap-1.5">
                  <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
                    All
                  </FilterChip>
                  <FilterChip
                    active={filter === 'unfinished'}
                    onClick={() => setFilter('unfinished')}
                  >
                    Unfinished
                  </FilterChip>
                  <FilterChip
                    active={filter === 'unassigned'}
                    onClick={() => setFilter('unassigned')}
                  >
                    Unassigned
                  </FilterChip>
                </div>
              }
            />

            {pending ? (
              <SkeletonRows rows={6} />
            ) : rows.length === 0 ? (
              <EmptyState
                icon="schedule"
                title={stops.length === 0 ? 'Nothing booked today' : 'No bookings match'}
                description={
                  stops.length === 0 ? 'Enjoy the quiet one.' : 'Try widening the filters.'
                }
              />
            ) : (
              <>
                <ListHead className={BOOKING_GRID}>
                  <span>Time</span>
                  <span>Job</span>
                  <span>Client</span>
                  <span>Work</span>
                  <span>Where</span>
                  <span>Status</span>
                  <span>Who</span>
                </ListHead>
                <ul className="divide-y divide-ink-100">
                  {rows.map((stop) =>
                    stop.kind === 'event' ? (
                      <li
                        key={stop.id}
                        className={cn(
                          'grid items-center gap-3 bg-ink-50/60 px-[var(--row-pad-x)] py-[var(--row-pad-y)]',
                          BOOKING_GRID,
                        )}
                      >
                        <span className="text-xs font-medium tabular-nums text-ink-500">
                          {formatTime(stop.startAt)}
                        </span>
                        <span className="truncate text-[10px] font-semibold tracking-wide text-ink-500 uppercase">
                          {EVENT_TYPE_LABEL[stop.event.type]}
                        </span>
                        <span className="truncate text-sm font-medium text-ink-700">
                          {stop.event.title}
                        </span>
                        <span />
                        <span className="truncate text-xs text-ink-500">
                          {stop.event.address?.suburb ?? stop.event.address?.city ?? 'Booked stop'}
                        </span>
                        <span>
                          <span className="rounded-full bg-ink-100 px-1.5 py-0.5 text-[10px] font-semibold text-ink-600">
                            {stop.event.completedAt ? 'Done' : EVENT_TYPE_LABEL[stop.event.type]}
                          </span>
                        </span>
                        <span className="text-xs text-ink-400">—</span>
                      </li>
                    ) : (
                      <li key={stop.id}>
                        <Link
                          to={jobHref(stop.visit.job.id)}
                          className={cn(
                            'grid items-center gap-3 px-[var(--row-pad-x)] py-[var(--row-pad-y)] transition-colors hover:bg-ink-50',
                            BOOKING_GRID,
                          )}
                        >
                          <span className="truncate text-xs tabular-nums">
                            <span className="font-semibold text-ink-800">
                              {formatTime(stop.visit.startAt)}
                            </span>
                            <span className="text-ink-400">
                              {' · '}
                              {durationBetween(stop.visit.startAt, stop.visit.endAt)}
                            </span>
                          </span>
                          <span className="truncate text-xs tabular-nums text-ink-500">
                            {formatJobNumber(stop.visit.job)}
                          </span>
                          <span className="truncate text-sm font-medium text-ink-900">
                            {stop.visit.job.client.name}
                          </span>
                          <span className="flex min-w-0 items-center gap-1.5 text-xs text-ink-600">
                            {stop.visit.job.category ? (
                              <>
                                <span
                                  aria-hidden="true"
                                  className="size-2 shrink-0 rounded-full"
                                  style={{ backgroundColor: stop.visit.job.category.colour }}
                                />
                                <span className="truncate">{stop.visit.job.category.name}</span>
                              </>
                            ) : null}
                          </span>
                          <span className="truncate text-xs text-ink-500">
                            {suburbOf(stop.visit) ?? stop.visit.job.site?.name ?? '—'}
                          </span>
                          <span>
                            <Badge tone={VISIT_STATUS_TONE[stop.visit.status] ?? 'neutral'}>
                              {humanise(stop.visit.status)}
                            </Badge>
                          </span>
                          <span className="flex min-w-0 items-center gap-1.5">
                            {stop.visit.assignments.length === 0 ? (
                              <span className="text-xs text-ink-400">&mdash;</span>
                            ) : (
                              <>
                                <Avatar
                                  name={stop.visit.assignments[0]!.user.name}
                                  colour={stop.visit.assignments[0]!.user.colour}
                                  size="sm"
                                />
                                <span className="truncate text-xs text-ink-700">
                                  {shortName(stop.visit.assignments[0]!.user.name)}
                                  {stop.visit.assignments.length > 1
                                    ? ` +${stop.visit.assignments.length - 1}`
                                    : ''}
                                </span>
                              </>
                            )}
                          </span>
                        </Link>
                      </li>
                    ),
                  )}
                </ul>
              </>
            )}
          </Card>
        </div>

        <NeedsAttention />
      </div>
    </div>
  )
}

const DOT: Record<Tone, string> = {
  neutral: 'bg-ink-400',
  blue: 'bg-blue-600',
  green: 'bg-emerald-600',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  purple: 'bg-purple-600',
}

/**
 * The office's to-do list, outside today.
 *
 * The Jobs list's queues that ask for something, the money that is late and
 * the mail nobody has read — each a count and a link to exactly that view, so
 * the morning starts from a list of things to do rather than a tour of five
 * screens. Every count is the same rule its destination filters on.
 */
function NeedsAttention() {
  const trpc = useTRPC()
  const { can } = useSession()

  const counts = useQuery({
    ...trpc.jobs.worklistCounts.queryOptions({}),
    enabled: can('JOB_VIEW'),
  })
  const owed = useQuery({
    ...trpc.invoices.summary.queryOptions(),
    enabled: can('INVOICE_VIEW'),
  })
  const unread = useQuery({
    ...trpc.inbox.unreadCount.queryOptions(),
    enabled: can('INBOX_VIEW'),
  })

  const items: Array<{
    key: string
    label: string
    meaning: string
    tone: Tone
    count: number | undefined
    to: string
  }> = WORKLIST_STATUSES.filter((entry) => entry.action !== null).map((entry) => ({
    key: entry.key,
    label: entry.action!,
    meaning: entry.meaning.charAt(0).toUpperCase() + entry.meaning.slice(1),
    tone: entry.tone,
    count: counts.data?.byStatus[entry.key],
    to: `/jobs${encodeSearch(jobListSchema, {
      ...jobListSchema.parse({}),
      tab: entry.tab,
      worklist: entry.key,
    })}`,
  }))

  if (can('INVOICE_VIEW')) {
    items.push({
      key: 'overdue',
      label: 'Chase overdue invoices',
      meaning: owed.data ? `${moneyFormat(owed.data.overdue.value)} past due` : 'Past their due date',
      tone: 'red',
      count: owed.data?.overdue.count,
      to: '/invoices?overdueOnly=true',
    })
  }

  if (can('INBOX_VIEW')) {
    items.push({
      key: 'inbox',
      label: 'Read new messages',
      meaning: 'Email and SMS from customers',
      tone: 'blue',
      count: unread.data,
      to: '/inbox',
    })
  }

  const waiting = items.filter((item) => (item.count ?? 0) > 0)

  return (
    <Card>
      <CardHeader title="Needs attention" />
      {counts.isPending && can('JOB_VIEW') ? (
        <SkeletonRows rows={4} />
      ) : waiting.length === 0 ? (
        <EmptyState icon="check" title="All caught up" description="Nothing is waiting on the office." />
      ) : (
        <ul className="divide-y divide-ink-100">
          {waiting.map((item) => (
            <li key={item.key}>
              <Link
                to={item.to}
                title={item.meaning}
                className="group flex items-center gap-2.5 px-[var(--row-pad-x)] py-[var(--row-pad-y)] transition-colors hover:bg-ink-50"
              >
                <span className={cn('size-2 shrink-0 rounded-full', DOT[item.tone])} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-ink-900">{item.label}</span>
                  <span className="block truncate text-[11px] text-ink-500">{item.meaning}</span>
                </span>
                <span className="shrink-0 rounded-full bg-ink-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-ink-800 transition-colors group-hover:bg-brand-800 group-hover:text-white">
                  {(item.count ?? 0).toLocaleString('en-AU')}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

/**
 * The one coloured left border in the system, and it is information rather
 * than decoration: somebody is on the clock right now.
 */
function OnSiteNow({
  visit,
  timer,
}: {
  visit: Visit | undefined
  timer: RouterOutputs['jobs']['runningTimer'] | undefined
}) {
  const jobHref = useJobHref()
  const job = visit?.job ?? timer?.job
  if (!job) return null

  const since = visit?.arrivedAt ?? visit?.startAt ?? timer?.startAt
  const who = visit?.assignments[0]?.user.name

  return (
    <Card className="flex items-center gap-4 border-l-4 border-l-green-500 px-[var(--card-pad)] py-2">
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium tracking-wide text-green-700 uppercase">
          On site now
        </p>
        <p className="mt-0.5 truncate text-sm font-semibold text-ink-900">
          {who ? `${who} · ` : ''}
          {job.title ?? job.client.name} ·{' '}
          <span className="font-mono text-xs text-ink-500">{formatJobNumber(job)}</span>
        </p>
        {visit?.job.site ? (
          <p className="truncate text-xs text-ink-500">
            {addressLine(visit.job.site.address)}
            {since ? ` · since ${formatTime(since)}` : ''}
          </p>
        ) : null}
      </div>
      {since ? (
        <span className="shrink-0 text-sm font-semibold tabular-nums text-green-700">
          {durationBetween(since)}
        </span>
      ) : null}
      <Link to={jobHref(job.id)} className={buttonClass({ size: 'sm' })}>
        Open job
      </Link>
    </Card>
  )
}
